import {
  Ion,
  Viewer as CesiumViewer,
  Cartesian3,
  Color,
  CesiumTerrainProvider,
  viewerDragDropMixin,
  VerticalOrigin,
  HorizontalOrigin,
  createGooglePhotorealistic3DTileset,
  KmlDataSource,
  PropertyBag,
  GeoJsonDataSource,
} from "cesium";
import { CesiumComponentRef, Viewer } from "resium";
import { useContext, useEffect, useRef, useState } from "react";
import { environment } from "../../../../common/env";
import { IMap } from "../../../../common/components/map/models";
import { MapSettings } from "../../../../common/components/resiumMap/mapSettings";
import { IShapeFile } from "../../../earthtwin/models";
import Context from "../../../../common/components/utils/context";
import { Themes } from "../../../../common/enums/themes";
import { Utils } from "../../../../common/helpers/utils";
import shp from "shpjs";
import dxfParser from "dxf-parser";
import {
  POIsV1Service,
  ShapeFilesV1Service,
} from "../../../../common/services/api/api";
import { IPOI } from "../../../matterportViewer/matterportViewer3";

export const PostMapView = (props: IMap) => {
  Ion.defaultAccessToken = environment.Cesium_Default_Access_Token;
  const context = useContext(Context);

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);

  const [terrainProvider, setTerrainProvider] =
    useState<CesiumTerrainProvider>();
  const [latitude, setLatitude] = useState<number>();
  const [longitude, setLongitude] = useState<number>();
  const [altitude, setAltitude] = useState<number>();
  const [enable3DMap, setEnable3DMap] = useState<boolean>(false);
  const [isShowLables, setIsShowLables] = useState<boolean>(true);

  useEffect(() => {
    const postLocation = props.locations ?? [];
    if (postLocation) {
      setLatitude(postLocation[0].latitude);
      setLongitude(postLocation[0].longitude);
      setAltitude(postLocation[0].altitude);
    }
  }, [props.locations]);

  useEffect(() => {
    props.callback?.();
  }, [props.callback]);

  useEffect(() => {
    if (latitude && longitude && altitude) {
      loadElevation(latitude, longitude);
    }
  }, [latitude, longitude, altitude]);

  const loadElevation = async (latitude: number, longitude: number) => {
    const height = await Utils.getElevation(latitude, longitude);

    props.viewportLatitude?.(latitude);
    props.viewportLongitude?.(longitude);
    props.viewportAltitude?.(height);
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.entities.add({
        position: Cartesian3.fromDegrees(longitude, latitude, 0),
        billboard: {
          image: "/img/pin_blue.svg",
          width: 44,
          height: 44,
          color: Color.WHITE.withAlpha(1.0),
          verticalOrigin: VerticalOrigin.BOTTOM,
          horizontalOrigin: HorizontalOrigin.CENTER,
        },
      });
      ref.current.cesiumElement.camera.flyTo({
        destination: Cartesian3.fromDegrees(longitude, latitude, height + 300),
        duration: 4,
      });
    }
  };

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      viewer.baseLayerPicker.viewModel.selectedImagery =
        viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];
    }
  }, [ref.current?.cesiumElement]);

  useEffect(() => {
    if (props.shapeFileId) getShapeFile(props.shapeFileId);
  }, [props.shapeFileId]);

  useEffect(() => {
    if (props.poiFileId) getPOIFile(props.poiFileId);
  }, [props.poiFileId]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.baseLayerPicker.viewModel.selectedImagery = isShowLables
        ? viewer.baseLayerPicker.viewModel.imageryProviderViewModels[1]
        : viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];
    }
  }, [ref.current?.cesiumElement, isShowLables]);

  const handleShowLablesToggle = () => setIsShowLables(!isShowLables);

  const handleEnable3DMapToggle = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      if (enable3DMap) {
        if (viewer.scene.primitives.length > 0) {
          for (let i = 0; i < viewer.scene.primitives.length; i++) {
            const primitive = viewer.scene.primitives.get(i);
            if (primitive && primitive.isCesium3DTileset) {
              viewer.scene.primitives.remove(primitive);
            }
          }
        }

        setEnable3DMap(false);
      } else {
        const tileset = await createGooglePhotorealistic3DTileset({
          key: environment.Google_Api_Key,
        });
        viewer.scene.primitives.add(tileset);

        setEnable3DMap(true);
      }
    }
  };

  const getPOIFile = async (id: string) => {
    try {
      const poiService = new POIsV1Service();
      const response = await poiService.get(id);
      loadPOIFile(response.data);
    } catch (error) {
      console.error("Error fetching POI file:", error);
    }
  };

  const loadPOIFile = async (poiFile: IPOI) => {
    console.log("Selected POI file:", poiFile);
    if (!ref.current || !ref.current.cesiumElement) return;
    const viewer = ref.current.cesiumElement;

    const entity = viewer.entities.getById(poiFile.id);
    if (entity) {
      viewer.entities.remove(entity);
    }

    const position = Cartesian3.fromDegrees(
      poiFile.longitude,
      poiFile.latitude,
      poiFile.altitude
    );

    const orientation = Utils.calculatePOIOrientation(poiFile);

    viewer.entities.add({
      id: poiFile.id,
      name: poiFile.name,
      position: position,
      model: {
        uri: poiFile.modelUrl,
        minimumPixelSize: 10,
        maximumScale: 256,
      },
      orientation: orientation,
    });

    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        poiFile.longitude,
        poiFile.latitude,
        poiFile.altitude + 100
      ),
    });
  };

  const getShapeFile = async (id: string) => {
    try {
      const shapeFileService = new ShapeFilesV1Service();
      const response = await shapeFileService.get(id);
      if (response.data) {
        await loadShapeFile(response.data);
      }
    } catch (error) {
      console.error("Error fetching shape file:", error);
    }
  };

  const loadShapeFile = async (shapeFile: IShapeFile) => {
    console.log("Selected file:", shapeFile);

    var view2DObjectResult = await view2DObjectByUrl(shapeFile);
    if (view2DObjectResult && view2DObjectResult.errorMessage) {
      context.setAlert({
        message: `Failed to display object!
                   ${view2DObjectResult.errorMessage}`,
        theme: Themes.Danger,
      });
    }
  };

  const view2DObjectByUrl = async (shapeFile: IShapeFile) => {
    var blob = await Utils.url2blob(shapeFile.url);
    if (blob) {
      var fileName = shapeFile.url.split("/").pop() || "";
      var files = [new File([blob], fileName)];
      const file = files[0];

      return await view2DObject(file, shapeFile.id);
    }
  };

  const view2DObject = async (file: File, id: string) => {
    if (!ref.current || !ref.current.cesiumElement) return;

    const viewer = ref.current.cesiumElement;

    var errorMessage;

    const fileExtension = file.name.split(".").pop()?.toLocaleLowerCase();
    if (fileExtension === "geojson") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const geojson = JSON.parse(e.target.result);

        const geojsonDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(geojsonDataSource);
      };

      reader.readAsText(file);
    } else if (fileExtension === "zip") {
      try {
        const data = await file.arrayBuffer();
        const geojson = await shp(data);

        const shpDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(shpDataSource);

        var entities = shpDataSource.entities.values;
        for (let entity of entities) {
          entity.name = file.name;
          entity.properties = new PropertyBag({ itemId: id });
        }

        var zoomedToSHP = viewer.zoomTo(shpDataSource);
        if (!zoomedToSHP) {
          errorMessage = "Unable to load SHP file!";
        }
      } catch (error) {
        errorMessage = "Unable to load SHP file!";
      }
    } else if (fileExtension === "dxf") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const parser = new dxfParser();
          const dxfData = parser.parseSync(e.target.result);
          const geoJSONData = Utils.convertDXFToGeoJSON(dxfData);
          const dxfDataSource = await GeoJsonDataSource.load(geoJSONData, {
            clampToGround: true,
          });
          await viewer.dataSources.add(dxfDataSource);

          var entities = dxfDataSource.entities.values;
          for (let entity of entities) {
            entity.name = file.name;
            entity.properties = new PropertyBag({ itemId: id });
          }

          var zoomedToDXF = viewer.zoomTo(dxfDataSource);
          if (!zoomedToDXF) {
            errorMessage = "Unable to load DXF file!";
          }
        } catch (error) {
          errorMessage = "Unable to load DXF file!";
        }
      };
      reader.readAsText(file);
    } else if (fileExtension === "kml" || fileExtension === "kmz") {
      try {
        const kmlDataSource = await KmlDataSource.load(file, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas,
          clampToGround: true,
        });
        await viewer.dataSources.add(kmlDataSource);
        var zoomedToKML = await viewer.zoomTo(kmlDataSource);
        if (!zoomedToKML) {
          errorMessage = "Unable to load KML file!";
        }
      } catch (error) {
        errorMessage = "Unable to load KML file!";
      }
    } else {
      errorMessage = "No valid .zip, .dxf, .kml or .kmz file!";
    }

    var newFileObj = {
      inputFile: file,
      errorMessage: errorMessage,
    };
    return newFileObj;
  };

  return (
    <>
      <div
        className="fixed top-20 left-10 w-[300px] bg-transparent flex flex-col gap-2"
        style={{ zIndex: 10 }}
      >
        <MapSettings
          onShowLablesToggle={handleShowLablesToggle}
          onEnable3DMapToggle={handleEnable3DMapToggle}
          isEnable3DMap={enable3DMap}
          isShowLables={isShowLables}
        />
      </div>
      <Viewer
        className={`relative h-full ${props.className ?? "w-full"}`}
        ref={ref}
        animation={false}
        timeline={false}
        homeButton={true}
        sceneModePicker={false}
        baseLayerPicker={true}
        navigationHelpButton={false}
        shouldAnimate={true}
        requestRenderMode={true}
        terrainProvider={terrainProvider}
        extend={viewerDragDropMixin}
      ></Viewer>
    </>
  );
};
